import {
  getAdminDatas,
  getUsersChartStats,
  getProjectCategoryStats,
  getDepartments,
  getEmployeeEvents,
  getTodayAttendanceSummaryFromAMS,
} from '../../../../../api/DashboardAPI';
import {
  getDashboardTodayAttendanceSummary,
  getAttendanceSummary,
  getDashboardSummary,
  getWeeklyComparison,
} from '../../../../../api/AttendanceAPI';
import { getLeaveEmails } from '../../../../../api/LeaveAPI';
import { getAdminAssets, getAssets } from '../../../../../api/AssetsAPI';
import { getIncidentStats, getIncidents } from '../../../../../api/IncidentAPI';
import { getActiveUsers } from '../../../../../api/AdminAPI';
import { getDashboardUsers, getUserById } from "../../../../../api/AdminAPI";
import { getNotificationsAPI } from '../../../../../api/NotificationAPI';
import { getProjectCodes } from '../../../../../api/ProjectCodeAPI';
import { setupAxios } from '../../../../modules/auth/core/AuthHelpers';
import { getJobPosts } from '../../../../../api/HiringAPI';
// import { getUsers } from "../../../../../services/UserAPI";
import { getUsers } from '../../../../../api/AdminAPI';

export interface DashboardKPIData {
  totalEmployees: number;
  presentToday: number;
  employeesOnLeave: number;
  activeProjects: number;
  openIncidents: number;
  totalAssets: number;
  attendanceRate: number;
  projectCompletionRate: number;
}

export interface WeeklyAttendanceData {
  thisWeek: number[];
  lastWeek: number[];
  labels: string[];
}

export interface LeaveRequestData {
  approved: number;
  pending: number;
  rejected: number;
}



export interface HiringPipelineData {
  open: number;
  closed: number;
  onHold: number;
}

export interface DepartmentData {
  name: string;
  employees: number;
}

export interface ProjectStatusData {
  active: number;
  onHold: number;
  completed: number;
  delayed: number;
}

export interface AssetUtilizationData {
  category: string;
  inUse: number;
  available: number;
}

export interface IncidentSeverityData {
  low: number;
  medium: number;
  high: number;
  critical: number;
}

export const INCIDENT_SEVERITY_CONFIG = [
  { key: 'critical', name: 'Critical', color: '#8b5cf6' },
  { key: 'high', name: 'High', color: '#ef4444' },
  { key: 'medium', name: 'Medium', color: '#fbbf24' },
  { key: 'low', name: 'Low', color: '#10b981' },
] as const;

export interface TodayLeaveData {
  name: string;
  type: string;
  duration: string;
}

export interface BirthdayData {
  name: string;
  date: string;
  department: string;
}

export interface AnniversaryData {
  name: string;
  years: number;
  department: string;
}

export interface ActivityData {
  action: string;
  time: string;
}

export interface AnnouncementData {
  title: string;
  date: string;
  priority: 'high' | 'medium' | 'low';
}

export interface DashboardData {
  kpi: DashboardKPIData;
  weeklyAttendance: WeeklyAttendanceData;
  leaveRequests: LeaveRequestData;
  hiringPipeline: HiringPipelineData;
  departments: DepartmentData[];
  projectStatus: ProjectStatusData;
  assetUtilization: AssetUtilizationData[];
  incidentSeverity: IncidentSeverityData;
  todayLeave: TodayLeaveData[];
  upcomingBirthdays: BirthdayData[];
  workAnniversaries: AnniversaryData[];
  recentActivities: ActivityData[];
  announcements: AnnouncementData[];
}

// Helper function to calculate percentage change
const calculateTrend = (current: number, previous: number): string => {
  if (previous === 0) return '+0%';
  const change = ((current - previous) / previous) * 100;
  return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
};

// Fetch KPI data
export const fetchKPIData = async (): Promise<DashboardKPIData> => {
  try {
    const [users, attendance, projects, incidents, assets] = await Promise.all([
      getActiveUsers().catch(() => ({ data: [] })),
      getDashboardTodayAttendanceSummary().catch(() => ({ data: { counts: {} } })),
      getProjectCodes().catch(() => ({ data: [] })),
      getIncidents({
        page: 1,
        limit: 1000,
      }).catch(() => ({
        data: {
          incidents: [],
        },
      })),
      getAssets().catch(() => ({ data: [] })),
    ]);

    const totalEmployees = users?.data?.length || 0;
    const attendanceCounts = attendance?.data?.counts || {};
    console.log("Dashboard Attendance API", attendance);
    const presentToday = attendanceCounts?.present || 0;
    const employeesOnLeave = attendanceCounts?.earlyLeave || 0;
    
    const projectData = projects?.data || [];
    const activeProjects = projectData.length || 0;
    
    const incidentList = incidents?.data?.incidents || [];
    const openIncidents = incidentList.filter((incident: any) => {
      return incident.closedAt === null;
    }).length;
    console.log("Incident List:", incidentList);
    console.log("Open Incidents:", openIncidents);
    
    const assetData = assets?.data || [];
    const totalAssets = assetData.length || 0;

    const attendanceRate = totalEmployees > 0 ? (presentToday / totalEmployees) * 100 : 0;
    const projectCompletionRate = projectData.length > 0 
      ? (projectData.filter((p: any) => p.status === 'completed').length / projectData.length) * 100 
      : 0;

    return {
      totalEmployees,
      presentToday,
      employeesOnLeave,
      activeProjects,
      openIncidents,
      totalAssets,
      attendanceRate,
      projectCompletionRate,
    };
  } catch (error) {
    console.error('Error fetching KPI data:', error);
    return {
      totalEmployees: 0,
      presentToday: 0,
      employeesOnLeave: 0,
      activeProjects: 0,
      openIncidents: 0,
      totalAssets: 0,
      attendanceRate: 0,
      projectCompletionRate: 0,
    };
  }
};

// Fetch weekly attendance data
export const fetchWeeklyAttendance = async (): Promise<WeeklyAttendanceData> => {
  try {
    const weeklyComparison = await getWeeklyComparison().catch(() => ({ data: {} }));
    
    const data = weeklyComparison?.data || {};
    const thisWeek = data?.thisWeek || [0, 0, 0, 0, 0, 0, 0];
    const lastWeek = data?.lastWeek || [0, 0, 0, 0, 0, 0, 0];
    const labels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    return {
      thisWeek,
      lastWeek,
      labels,
    };
  } catch (error) {
    console.error('Error fetching weekly attendance:', error);
    return {
      thisWeek: [0, 0, 0, 0, 0, 0, 0],
      lastWeek: [0, 0, 0, 0, 0, 0, 0],
      labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    };
  }
};

// Fetch leave request data
export async function fetchLeaveRequests() {
  const { id } = setupAxios();
  const res: any = await getLeaveEmails(id, "receiver");
  const leaves = res.data || [];
  return {
    approved: leaves.filter((x: any) => ['APPROVED', 'AUTO_APPROVED'].includes(x.status)).length,
    pending: leaves.filter((x: any) => x.status === "PENDING").length,
    rejected: leaves.filter((x: any) => x.status === "REJECTED").length,
  };
}

// Fetch hiring pipeline data
export interface HiringPipelineData {
  open: number;
  closed: number;
  onHold: number;
}

export async function fetchHiringPipeline(): Promise<HiringPipelineData> {
  try {
    const response: any = await getJobPosts({
      limit: 100,
      offset: 0,
      isPublished: true,
    });
    const jobs = response.data || [];
    return {
      open: jobs.filter((job: any) => job.status === "OPEN").length,
      closed: jobs.filter((job: any) => job.status === "CLOSED").length,
      onHold: jobs.filter((job: any) => job.status === "ON_HOLD").length,
    };
  } catch (error) {
    console.error("Error fetching hiring pipeline:", error);
    return {
      open: 0,
      closed: 0,
      onHold: 0,
    };
  }
}

// Fetch department data
export const fetchDepartments = async (): Promise<DepartmentData[]> => {
  try {
    const departments = await getDepartments().catch(() => ({ data: [] }));
    const deptData = departments?.data || [];

    return deptData.map((dept: any) => ({
      name: dept.name || dept.departmentName || 'Unknown',
      employees: dept.employeeCount || dept.userCount || 0,
    }));
  } catch (error) {
    console.error('Error fetching departments:', error);
    return [];
  }
};

// Fetch project status data
export async function fetchProjectStatus(): Promise<ProjectStatusData> {
  try {
    const response: any = await getProjectCodes();
    console.log("Project Response:", response);
    const projects = response.data || [];
    return {
      active: projects.filter((p: any) => p.status === "ACTIVE").length,
      onHold: projects.filter((p: any) => p.status === "ON_HOLD").length,
      completed: projects.filter((p: any) => p.status === "COMPLETED").length,
      delayed: projects.filter((p: any) => p.status === "DELAYED").length,
    };
  } catch (error) {
    console.error(error);
    return {
      active: 0,
      onHold: 0,
      completed: 0,
      delayed: 0,
    };
  }
}

// Fetch asset utilization data
export const fetchAssetUtilization = async (): Promise<AssetUtilizationData[]> => {
  try {
    const assets = await getAssets().catch(() => ({ data: [] }));
    const assetData = assets?.data || [];
    console.log('Asset data:', assetData);

    // Group by category
    const categoryMap = new Map<string, { inUse: number; available: number; other: number }>();
    
    assetData.forEach((asset: any) => {
      const category = asset.category || 'Other';
      const status = asset.status || 'Available';
      
      if (!categoryMap.has(category)) {
        categoryMap.set(category, { inUse: 0, available: 0, other: 0 });
      }
      
      const counts = categoryMap.get(category)!;
      
      // Based on Asset module status values: Available, Assigned, Acknowledged, UnderMaintenance, Decommissioned, Retired, Lost, Damaged
      switch (status) {
        case 'Assigned':
        case 'Acknowledged':
          counts.inUse++;
          break;
        case 'Available':
          counts.available++;
          break;
        default:
          // UnderMaintenance, Decommissioned, Retired, Lost, Damaged
          counts.other++;
          break;
      }
    });

    const result = Array.from(categoryMap.entries()).map(([category, counts]) => ({
      category,
      inUse: counts.inUse,
      available: counts.available + counts.other, // Include other statuses in available for simplicity
    }));
    
    console.log('Asset utilization result:', result);
    return result;
  } catch (error) {
    console.error('Error fetching asset utilization:', error);
    return [];
  }
};

// Fetch incident severity data
export const fetchIncidentSeverity = async (): Promise<IncidentSeverityData> => {
  try {
    const incidentStats = await getIncidentStats().catch(() => ({ data: {} }));
    console.log('Incident stats API response:', incidentStats);
    const stats = incidentStats?.data || {};
    console.log('Stats data:', stats);
    
    // The API returns byPriority as an array of { priority: string, _count: number }
    const byPriority = stats.byPriority || [];
    console.log('By priority data:', byPriority);
    
    // Initialize counts
    let low = 0, medium = 0, high = 0, critical = 0;
    
    // Parse the byPriority array
    byPriority.forEach((item: any) => {
      const priority = String(item.priority || '').toLowerCase();
      const rawCount = item._count;
      const count = typeof rawCount === 'number'
        ? rawCount
        : Number(rawCount?._all ?? rawCount?.priority ?? rawCount?.id ?? 0) || 0;
      
      switch (priority) {
        case 'low':
          low = count;
          break;
        case 'medium':
          medium = count;
          break;
        case 'high':
          high = count;
          break;
        case 'critical':
          critical = count;
          break;
      }
    });
    
    const result = { low, medium, high, critical };
    console.log('Final incident severity result:', result);
    return result;
  } catch (error) {
    console.error('Error fetching incident severity:', error);
    return { low: 0, medium: 0, high: 0, critical: 0 };
  }
};
export async function fetchEmployeeDistribution() {
  try {
    const response: any = await getUsers();
    const users = response.data?.items || [];
    const departmentMap: Record<string, number> = {};
    users.forEach((user: any) => {
      const dept = user.department || "Others";
      departmentMap[dept] = (departmentMap[dept] || 0) + 1;
    });
    return Object.entries(departmentMap).map(([name, value]) => ({
      name,
      value,
    }));
  } catch (error) {
    console.error("Employee Distribution Error:", error);
    return [];
  }
}

// Fetch today's leave data
export const fetchTodayLeave = async (): Promise<TodayLeaveData[]> => {
  try {
    const { id } = setupAxios();

    const response: any = await getLeaveEmails(id, "receiver");
    const leaves = response?.data || [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const next5Days = new Date(today);
    next5Days.setDate(today.getDate() + 5);

    return leaves
      .filter((leave: any) => {
        if (!['APPROVED', 'AUTO_APPROVED'].includes(leave.status)) return false;

        const startDate = new Date(leave.startDate);
        const endDate = new Date(leave.endDate);

        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);

        // Show leaves from today to next 5 days
        return startDate <= next5Days && endDate >= today;
      })
      .map((leave: any) => ({
        name: `${leave.user?.firstName || ""} ${leave.user?.lastName || ""}`,
        type: leave.leaveType?.name || "Leave",
        duration: `${new Date(leave.startDate).toLocaleDateString()} - ${new Date(
          leave.endDate
        ).toLocaleDateString()}`,
      }));
  } catch (error) {
    console.error("Error fetching today's leave:", error);
    return [];
  }
};

// Fetch upcoming birthdays
export const fetchUpcomingBirthdays = async (): Promise<BirthdayData[]> => {
  try {
    const response = await getDashboardUsers();
    const users = response?.data?.items || [];

    const today = new Date();

    return users
      .filter((user: any) => user.dateOfBirth)
      .map((user: any) => {
        const dob = new Date(user.dateOfBirth);

        let nextBirthday = new Date(
          today.getFullYear(),
          dob.getMonth(),
          dob.getDate()
        );

        if (nextBirthday < today) {
          nextBirthday.setFullYear(today.getFullYear() + 1);
        }

        return {
          ...user,
          nextBirthday,
        };
      })
      .filter((user: any) => {
        const diffDays =
          (user.nextBirthday.getTime() - today.getTime()) /
          (1000 * 60 * 60 * 24);

        return diffDays >= 0 && diffDays <= 30;
      })
      .sort(
        (a: any, b: any) =>
          a.nextBirthday.getTime() - b.nextBirthday.getTime()
      )
      .slice(0, 5)
      .map((user: any) => ({
        name: `${user.firstName} ${user.lastName}`,
        department: user.department?.name || "Unknown",
        date: new Date(user.dateOfBirth).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
      }));
  } catch (error) {
    console.error(error);
    return [];
  }
};

// Fetch work anniversaries
export const fetchWorkAnniversaries = async (): Promise<AnniversaryData[]> => {
  try {
    const users = await getDashboardUsers().catch(() => ({ data: { items: [] } }));
    const userData = users?.data?.items || [];
    console.log("Users:", userData);
    console.log("First User:", userData[0]);
    const today = new Date();

    return userData
      .filter((user: any) => {
        if (!user.joiningDate) return false;

        const joining = new Date(user.joiningDate);

        let nextAnniversary = new Date(
          today.getFullYear(),
          joining.getMonth(),
          joining.getDate()
        );

        if (nextAnniversary < today) {
          nextAnniversary.setFullYear(today.getFullYear() + 1);
        }

        return (
          joining.getMonth() === today.getMonth() &&
          joining.getDate() >= today.getDate()
        );
      })
      .sort((a: any, b: any) => {
        const da = new Date(a.joiningDate);
        const db = new Date(b.joiningDate);

        let aa = new Date(today.getFullYear(), da.getMonth(), da.getDate());
        let bb = new Date(today.getFullYear(), db.getMonth(), db.getDate());

        if (aa < today) aa.setFullYear(today.getFullYear() + 1);
        if (bb < today) bb.setFullYear(today.getFullYear() + 1);

        return aa.getTime() - bb.getTime();
      })
      .slice(0, 5)
      .map((user: any) => {
        const joining = new Date(user.joiningDate);

        return {
          name: `${user.firstName} ${user.lastName}`,
          years: today.getFullYear() - joining.getFullYear(),
          department: user.department || "Unknown",
        };
      });
  } catch (error) {
    console.error("Error fetching work anniversaries:", error);
    return [];
  }
};

// Fetch recent activities
export const fetchRecentActivities = async (): Promise<ActivityData[]> => {
  try {
    const events = await getEmployeeEvents().catch(() => ({ data: [] }));
    const eventData = events?.data || [];

    return eventData.slice(0, 5).map((event: any) => ({
      action: event.description || event.eventType || 'Activity',
      time: event.createdAt || event.timestamp || 'Recently',
    }));
  } catch (error) {
    console.error('Error fetching recent activities:', error);
    return [];
  }
};

// Fetch announcements
export const fetchAnnouncements = async (): Promise<AnnouncementData[]> => {
  try {
    const { id } = setupAxios(); // Logged-in user id

    const notifications = await getNotificationsAPI(id);
    const notificationData = notifications?.data || [];

    return notificationData
      .slice(0, 5)
      .map((notif: any) => ({
        title: notif.title || notif.message || "Announcement",
        date: new Date(notif.createdAt).toLocaleDateString(),
        priority: notif.priority || "medium",
      }));
  } catch (error) {
    console.error("Error fetching announcements:", error);
    return [];
  }
};

// Fetch all dashboard data
export const fetchDashboardData = async (): Promise<DashboardData> => {
  try {
    const [
      kpi,
      weeklyAttendance,
      leaveRequests,
      hiringPipeline,
      departments,
      projectStatus,
      assetUtilization,
      incidentSeverity,
      todayLeave,
      upcomingBirthdays,
      workAnniversaries,
      recentActivities,
      announcements,
    ] = await Promise.all([
      fetchKPIData(),
      fetchWeeklyAttendance(),
      fetchLeaveRequests(),
      fetchHiringPipeline(),
      fetchDepartments(),
      fetchProjectStatus(),
      fetchAssetUtilization(),
      fetchIncidentSeverity(),
      fetchTodayLeave(),
      fetchUpcomingBirthdays(),
      fetchWorkAnniversaries(),
      fetchRecentActivities(),
      fetchAnnouncements(),
    ]);

    return {
      kpi,
      weeklyAttendance,
      leaveRequests,
      hiringPipeline,
      departments,
      projectStatus,
      assetUtilization,
      incidentSeverity,
      todayLeave,
      upcomingBirthdays,
      workAnniversaries,
      recentActivities,
      announcements,
    };
  } catch (error) {
    console.error('Error fetching dashboard data:', error);
    throw error;
  }
};
